/**
 * WanSpeedIndicator for Wan local providers.
 *
 * Shows estimated generation time for the active variant/steps/frames on the
 * user's GPU. Mirror of estimate_wan_local_seconds() in
 * constants/video_generation/wan_local.py.
 *
 * Domain-specific name to avoid colliding with the LTX-2 `SpeedIndicator`
 * (common/LTX2ConfigPanel/SpeedIndicator.tsx), a different component (VRAM
 * real-time-ratio for LTX-2) re-exported via common/index.ts.
 */

import React from 'react';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { estimateWanSeconds, type WanVariant } from '@/constants/wanLocal';

interface WanSpeedIndicatorProps {
  variant: WanVariant;
  steps: number;
  numFrames: number;
  fps: number;
  vramGb: number | null;
}

export const WanSpeedIndicator: React.FC<WanSpeedIndicatorProps> = ({
  variant,
  steps,
  numFrames,
  fps,
  vramGb,
}) => {
  const { t } = useTranslation();
  const vram = vramGb ?? 0;

  const totalSeconds = estimateWanSeconds(variant, steps, numFrames, vram);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const minutesDisplay = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  const videoDurationS = (numFrames / fps).toFixed(1);

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/10">
      <Clock className="w-4 h-4 text-white/50 flex-shrink-0" />
      <span className="text-xs text-white/60">
        {t('wanLocal.speedIndicator.estimate', {
          defaultValue: '~{{time}} to generate {{seconds}}s video on your hardware',
          time: minutesDisplay,
          seconds: videoDurationS,
        })}
      </span>
    </div>
  );
};

export default WanSpeedIndicator;
